import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useThemeMode } from "../../ThemeContext";
import { APP_URLS, FILE_DOWNLOAD_PREFIX } from "../../helpers/url_helper";

const getAttachmentKind = (fileName, mimeType = "") => {
  const name = String(fileName || "").toLowerCase();
  const mime = String(mimeType || "").toLowerCase();
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return "image";
  if (
    mime === "application/msword" ||
    mime.includes("wordprocessingml") ||
    /\.(doc|docx)$/i.test(name)
  ) {
    return "document";
  }
  return "file";
};

const isSignatureSection = (section) =>
  String(section?.title || "").trim().toLowerCase() === "signature";

const resolveMediaUrl = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  if (value.startsWith("data:image/")) return value;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  const path = value.replace(/^\//, "");
  return `${FILE_DOWNLOAD_PREFIX}${path}`;
};

function SignatureAnswerPreview({ mode, label, imageSrc }) {
  const imageUrl = resolveMediaUrl(imageSrc);
  const safeLabel = String(label || "Signature").trim() || "Signature";

  return (
    <div style={answerCardStyle(mode)}>
      <div style={labelStyle(mode)}>{safeLabel}</div>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={safeLabel}
          style={signatureImageStyle(mode)}
        />
      ) : (
        <div style={valueStyle(mode)}>-</div>
      )}
    </div>
  );
}

function AttachmentAnswerRow({ mode, href, fileName, mimeType }) {
  const safeName = String(fileName || "Attachment").trim() || "Attachment";
  const kind = getAttachmentKind(safeName, mimeType);
  const iconColor =
    kind === "pdf"
      ? "#dc2626"
      : kind === "image"
        ? "#0d9488"
        : kind === "document"
          ? "#2563eb"
          : mode === "dark"
            ? "#94a3b8"
            : "#64748b";

  let FileIcon = InsertDriveFileOutlinedIcon;
  if (kind === "pdf") FileIcon = PictureAsPdfIcon;
  else if (kind === "image") FileIcon = ImageOutlinedIcon;
  else if (kind === "document") FileIcon = DescriptionIcon;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={safeName}
      aria-label={`Open attachment: ${safeName}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        maxWidth: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${mode === "dark" ? "#334155" : "#e2e8f0"}`,
        background: mode === "dark" ? "#0f172a" : "#f8fafc",
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        boxSizing: "border-box",
      }}
    >
      <FileIcon
        style={{
          fontSize: 36,
          color: iconColor,
          flexShrink: 0,
        }}
        aria-hidden
      />
      <span
        style={{
          minWidth: 0,
          flex: 1,
          fontSize: 13,
          fontWeight: 600,
          color: mode === "dark" ? "#e2e8f0" : "#1e293b",
          wordBreak: "break-word",
          lineHeight: 1.4,
        }}
      >
        {safeName}
      </span>
    </a>
  );
}

function CompanyFormRecordView() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const form = location.state?.form || null;
  const submission = location.state?.submission || null;
  const isFromDetailsPage =
    location.state?.source === "employee-details" ||
    location.state?.source === "asset-details" ||
    location.state?.source === "site-details" ||
    location.state?.source === "project-details";

  const handleBack = () => {
    if (isFromDetailsPage) {
      navigate(-1);
      return;
    }
    navigate(APP_URLS.COMPANY_FORM_RECORDS, { state: { form } });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const day = parsed.getDate();
    const month = parsed.getMonth() + 1;
    const year = parsed.getFullYear();
    const hours24 = parsed.getHours();
    const hours12 = hours24 % 12 || 12;
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const seconds = String(parsed.getSeconds()).padStart(2, "0");
    const meridiem = hours24 >= 12 ? "pm" : "am";
    return `${day}/${month}/${year}, ${hours12}:${minutes}:${seconds} ${meridiem}`;
  };

  const getSubmittedByName = (row) => {
    return (
      row?.submitted_by_full_name ||
      row?.submitted_by_name ||
      row?.submitted_by_user_full_name ||
      row?.submitted_user_full_name ||
      row?.submitted_by_user?.full_name ||
      row?.submitted_by_user?.name ||
      row?.submitted_by_details?.full_name ||
      row?.submitted_by_details?.name ||
      "-"
    );
  };

  const renderAnswerContent = (raw) => {
    if (raw == null || raw === "") return "-";
    if (Array.isArray(raw)) {
      return raw.length ? raw.join(", ") : "-";
    }
    const s = typeof raw === "string" ? raw : String(raw);
    if (s.trim().startsWith("{")) {
      try {
        const obj = JSON.parse(s);
        if (
          obj &&
          obj.kind === "formAttachment" &&
          typeof obj.url === "string" &&
          obj.url.trim()
        ) {
          const path = obj.url.trim().replace(/^\//, "");
          const href = path.startsWith("http") ? path : `${FILE_DOWNLOAD_PREFIX}${path}`;
          const displayName =
            String(obj.fileName || path.split("/").pop() || "attachment").trim() ||
            "attachment";
          const mimeHint =
            typeof obj.content_type === "string" ? obj.content_type : "";
          return (
            <AttachmentAnswerRow
              mode={mode}
              href={href}
              fileName={displayName}
              mimeType={mimeHint}
            />
          );
        }
      } catch {
        /* fall through */
      }
    }
    const trimmed = s.trim();
    if (
      trimmed &&
      trimmed.includes("digital_form_attachments/") &&
      !trimmed.startsWith("{")
    ) {
      const path = trimmed.replace(/^\//, "");
      const href = path.startsWith("http") ? path : `${FILE_DOWNLOAD_PREFIX}${path}`;
      const displayName = path.split("/").pop() || "attachment";
      return (
        <AttachmentAnswerRow mode={mode} href={href} fileName={displayName} />
      );
    }
    if (s.length > 400) {
      return `${s.slice(0, 400)}…`;
    }
    return s;
  };

  const parseAnswers = (rawAnswers) => {
    if (Array.isArray(rawAnswers)) return rawAnswers;
    if (typeof rawAnswers === "string") {
      try {
        const parsed = JSON.parse(rawAnswers);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const answers = parseAnswers(submission?.answers);
  const isSectionWise = Array.isArray(answers) && Array.isArray(answers[0]?.fields);

  if (!form || !submission) {
    return (
      <div style={{ padding: 20 }}>
        <div style={errorStyle(mode)}>
          Record details are missing. Please open from Form records page.
        </div>
        <button
          type="button"
          onClick={handleBack}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Records
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={wrapperStyle(mode)}>
        <button
          type="button"
          onClick={handleBack}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Records
        </button>

        <h2 style={titleStyle(mode)}>{form?.title || "Form Record"}</h2>
        <div style={metaRowStyle()}>
          <p style={metaStyle(mode)}>Submitted by: {getSubmittedByName(submission)}</p>
          <p style={metaStyle(mode)}>Submitted at: {formatDateTime(submission?.submitted_at)}</p>
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {isSectionWise &&
            answers.map((section, sectionIndex) => (
              <section
                key={`${section?.title || "section"}-${sectionIndex}`}
                style={sectionCardStyle(mode)}
              >
                <h3 style={sectionTitleStyle(mode)}>
                  {section?.title || `Section ${sectionIndex + 1}`}
                </h3>
                {section?.description ? (
                  <p style={sectionDescStyle(mode)}>{section.description}</p>
                ) : null}
                <div style={{ display: "grid", gap: 10 }}>
                  {(section?.fields || []).map((field, fieldIndex) => {
                    if (isSignatureSection(section)) {
                      return (
                        <SignatureAnswerPreview
                          key={`${field?.label || "field"}-${fieldIndex}`}
                          mode={mode}
                          label={field?.label || "Field"}
                          imageSrc={field?.answer}
                        />
                      );
                    }

                    return (
                      <div
                        key={`${field?.label || "field"}-${fieldIndex}`}
                        style={answerCardStyle(mode)}
                      >
                        <div style={labelStyle(mode)}>{field?.label || "Field"}</div>
                        <div style={valueStyle(mode)}>{renderAnswerContent(field?.answer)}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

          {!isSectionWise &&
            answers.map((answer, index) => (
              <div key={`${answer?.id || "answer"}-${index}`} style={answerCardStyle(mode)}>
                <div style={labelStyle(mode)}>{answer?.label || "Field"}</div>
                <div style={valueStyle(mode)}>
                  {renderAnswerContent(answer?.answer ?? answer?.value)}
                </div>
              </div>
            ))}

          {answers.length === 0 && (
            <div style={emptyStyle(mode)}>No submitted values found.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const wrapperStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 12,
});

const titleStyle = (mode) => ({
  margin: "0 0 6px",
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const metaStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#98a2b3" : "#64748b",
  fontSize: 13,
});

const metaRowStyle = () => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
  marginBottom: 4,
});

const answerCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#e2e8f0"}`,
  borderRadius: 10,
  padding: 12,
  background: mode === "dark" ? "#151515" : "#fff",
});

const sectionCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#e2e8f0"}`,
  borderRadius: 10,
  padding: 12,
  background: mode === "dark" ? "#111827" : "#f8fafc",
  display: "grid",
  gap: 10,
});

const sectionTitleStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#f8fafc" : "#0f172a",
  fontSize: 16,
  fontWeight: 700,
});

const sectionDescStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#94a3b8" : "#64748b",
  fontSize: 13,
});

const labelStyle = (mode) => ({
  color: mode === "dark" ? "#cbd5e1" : "#334155",
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 4,
});

const valueStyle = (mode) => ({
  color: mode === "dark" ? "#fff" : "#0f172a",
  fontSize: 14,
});

const errorStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#704040" : "#f1b2b2"}`,
  background: mode === "dark" ? "#2b1717" : "#fff3f3",
  color: mode === "dark" ? "#ffb0b0" : "#a53030",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: 14,
  marginBottom: 12,
});

const emptyStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const signatureImageStyle = (mode) => ({
  display: "block",
  maxWidth: "100%",
  width: "auto",
  maxHeight: 180,
  marginTop: 4,
  borderRadius: 8,
  border: `1px solid ${mode === "dark" ? "#334155" : "#e2e8f0"}`,
  background: "#fff",
  objectFit: "contain",
});

export default CompanyFormRecordView;
